import { FC } from 'react';
import { Box, LinearProgress, linearProgressClasses, styled, Typography } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import IconContainer from './Icon/IconContainer';
import Column from './Column';
import Row from './Row';
import Utils from './Utils';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 4,
    borderRadius: theme.spacing(1),
    [`&.${linearProgressClasses.root}`]: {
        backgroundColor: theme.palette.grey[300],
    },
}));

interface PacingProps {
    name?: string;
    actual: number;
    target: number;
    percentage?: number;
    unit?: string;
    compact?: boolean; // Add compact mode
    getColor?: (actual: number, target: number, percentage: number) => 'success' | 'warning' | 'error';
    getLabel?: (actual: number, target: number, percentage: number) => string;
}

const defaultGetColor = (actual: number, target: number, percentage: number): 'success' | 'warning' | 'error' => {
    return 'success';
};

const defaultGetLabel = (actual: number, target: number, percentage: number): string => {
    return '';
};

const Pacing: FC<PacingProps> = ({
    name = '',
    actual,
    target,
    percentage: defaultPercentage = 0,
    unit = '',
    compact = false,
    getColor = defaultGetColor,
    getLabel = defaultGetLabel,
}) => {
    const percentage = defaultPercentage > 0 ? defaultPercentage : target > 0 ? (actual / target) * 100 : 0;
    const color = getColor(actual, target, percentage);
    const label = getLabel(actual, target, percentage);

    if (compact) {
        return (
            <Box
                sx={{
                    backgroundColor: (theme) => theme.palette.grey[100],
                    p: 1.5,
                    borderRadius: 1,
                    minWidth: 200,
                }}
            >
                <Column gap={1}>
                    <Row justifyContent="space-between" alignItems="center">
                        <Row alignItems="center" gap={0.5}>
                            <IconContainer
                                icon={HistoryIcon}
                                color={color}
                                sx={{ borderWidth: 1, width: 20, height: 20 }}
                            />
                            {label && (
                                <Typography variant="caption" color="text.primary" fontWeight={500}>
                                    {label}
                                </Typography>
                            )}
                        </Row>
                        <Typography variant="caption" color="text.primary" fontWeight={500}>
                            {percentage.toFixed(1)}%
                        </Typography>
                    </Row>

                    <BorderLinearProgress variant="determinate" value={Math.min(percentage, 100)} color={color} />

                    <Row justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {name}
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                            {unit}
                            {Utils.formatNumber(target)}
                        </Typography>
                    </Row>
                </Column>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                backgroundColor: (theme) => theme.palette.grey[100],
                p: (theme) => theme.spacing(3),
                borderRadius: (theme) => theme.spacing(1),
            }}
        >
            <Column gap={2}>
                <Row justifyContent="space-between">
                    <Row alignItems="center">
                        <IconContainer icon={HistoryIcon} color={color} sx={{ borderWidth: 1 }} />

                        {label && (
                            <Typography variant="subtitle2" color="text.primary">
                                {label}
                            </Typography>
                        )}
                    </Row>

                    <Row alignItems="center" justifyContent="flex-end" gap={0.5}>
                        {name && (
                            <Typography variant="body2" color="text.secondary">
                                {name}:
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.primary">
                            {unit}
                            {Utils.formatNumber(target as number)}
                        </Typography>
                    </Row>
                </Row>

                <Row alignItems="center" gap={2}>
                    <Box sx={{ width: '100%' }}>
                        <BorderLinearProgress variant="determinate" value={Math.min(percentage, 100)} color={color} />
                    </Box>

                    <Typography variant="body2" color="text.primary">
                        {percentage.toFixed(1)}%
                    </Typography>
                </Row>
            </Column>
        </Box>
    );
};

export type { PacingProps };
export default Pacing;
